import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';

interface RouteLoaderProps {
  variant?: 'default' | 'minimal' | 'full' | 'dashboard';
  className?: string;
}

export const RouteLoader: React.FC<RouteLoaderProps> = ({ 
  variant = 'default', 
  className = '' 
}) => {
  const baseClasses = "min-h-screen flex items-center justify-center bg-background";
  
  switch (variant) {
    case 'minimal':
      return (
        <div className={`${baseClasses} ${className}`} data-testid="route-loader-minimal">
          <div className="flex items-center space-x-2" role="status" aria-live="polite">
            <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" aria-hidden="true" />
            <span className="text-sm text-muted-foreground">Loading...</span>
          </div>
        </div>
      );
      
    case 'full':
      return (
        <div className={`${baseClasses} ${className}`} data-testid="route-loader-full">
          <Card className="mx-4 w-full max-w-2xl">
            <CardContent className="space-y-6 pt-6">
              <div className="space-y-2">
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
              <div className="space-y-3">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
              <div className="flex space-x-2">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-24" />
              </div>
            </CardContent>
          </Card>
        </div>
      );
      
    case 'dashboard':
      return (
        <div className={`${baseClasses} ${className}`} data-testid="route-loader-dashboard">
          <div className="container mx-auto space-y-6 px-4 py-8">
            <div className="space-y-2">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-4 w-96" />
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="space-y-4 pt-6">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-32 w-full" />
                    <div className="flex space-x-2">
                      <Skeleton className="h-8 w-16" />
                      <Skeleton className="h-8 w-20" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      );
      
    default:
      return (
        <div className={`${baseClasses} ${className}`} data-testid="route-loader-default">
          <div className="mx-4 w-full max-w-md space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <div className="space-y-2 pt-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-2/3" />
            </div>
          </div>
        </div>
      );
  }
};

// Error boundary component for lazy loading failures
export class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ComponentType }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Route loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;
      
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Card className="mx-4 w-full max-w-md">
            <CardContent className="space-y-4 pt-6 text-center">
              <div className="text-destructive">
                <svg
                  className="mx-auto mb-4 size-12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold">Failed to load page</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Please refresh the page to try again.
                </p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="rounded-md bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Refresh Page
              </button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

